import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Container, Typography, Grid, TextField, MenuItem, Button, Box, Card, CardContent, IconButton, Paper, Snackbar, Alert, LinearProgress, DialogActions, DialogContent, DialogContentText, DialogTitle, Tooltip } from "@mui/material";
import AppDialog from "../../../../common/AppDialog";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { deleteData, getData, postData } from '../../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../../services/endpoints';

const documentCategories = {
    educational: {
        label: 'Educational Documents', types: [
            { key: 'metric', label: 'Metric (10th)', apiType: 'education' },
            { key: 'graduation', label: 'Graduation', apiType: 'education' },
            { key: 'postGraduation', label: 'Post Graduation', apiType: 'education' },
            { key: 'otherCertifications', label: 'Other Certifications', apiType: 'certification' }
        ]
    },
    work: {
        label: 'Work-Related Documents',
        types: [
            { key: 'offerLetter', label: 'Offer Letter', apiType: 'offer_letter' },
            { key: 'appointmentLetter', label: 'Appointment Letter', apiType: 'appointment_letter' },
            { key: 'payslips', label: 'Last 3 Months Payslip (Combined)', apiType: 'payslip' },
            { key: 'experienceLetter', label: 'Experience Letter', apiType: 'experience_letter' },
            { key: 'hikeLetter', label: 'Hike Letter', apiType: 'hike_letter' },
            { key: 'hikeWithPromotion', label: 'Hike with Promotion', apiType: 'hike_promotion_letter' },
            { key: 'relocation', label: 'Relocation Letter', apiType: 'relocation_letter' },
            { key: 'nda', label: 'NDA (Non-Disclosure Agreement)', apiType: 'nda' }
        ]
    },
    identity: {
        label: 'Identity Documents', types: [
            { key: 'pan', label: 'PAN Card', apiType: 'pan_card' },
            { key: 'aadhar', label: 'Aadhar Card(Upload Front and Back)', apiType: 'aadhar_card' },
            { key: 'photo', label: 'Passport Photo', apiType: 'photo' },
            { key: 'drivingLicence', label: 'Driving Licence', apiType: 'driving_licence' }
        ]
    },
    others: { label: 'Other Documents', types: [{ key: 'misc', label: 'Miscellaneous Document', apiType: 'misc' }] },
};

const getDocumentTypeInfo = (typeKey) => {
    for (const [categoryKey, category] of Object.entries(documentCategories)) {
        const type = category.types.find(t => t.key === typeKey);
        if (type) return { ...type, categoryKey };
    }
    return null;
};

const apiTypeToFrontendKey = Object.values(documentCategories).flatMap(category =>
    category.types.map(type => ({ [type.apiType]: type.key }))
).reduce((acc, current) => ({ ...acc, ...current }), {});

const mockInitialData = {
    documents: {},
};

const handleDownload = (document) => {
    window.open(document.url, '_blank');
};

const handlePreview = (document) => {
    window.open(document.url, '_blank');
};

const EmployeeDocuments = ({ initialData = mockInitialData, userId, navigationContext, onSaveAndReturn, onFormCompletionChange }) => {
    const [documents, setDocuments] = useState({});
    const [fileToUpload, setFileToUpload] = useState(null);
    const [selectedUploadType, setSelectedUploadType] = useState('');
    const [activeUploadCategory, setActiveUploadCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const Token = localStorage.getItem('accessToken')
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        docId: null,
        typeKey: null,
        fileName: '',
    });

    const [completion, setCompletion] = useState(0);
    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar({ ...snackbar, open: false });
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const resetUploadState = () => {
        setFileToUpload(null);
        setSelectedUploadType('');

        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    const flatDocumentOptions = useMemo(() =>
        Object.entries(documentCategories).flatMap(([categoryKey, categoryInfo]) =>
            categoryInfo.types.map(type => ({
                key: type.key,
                label: type.label,
                category: categoryInfo.label,
                categoryKey: categoryKey,
                apiType: type.apiType,
            }))
        ), []
    );

    const getCategoryOptions = (categoryKeys) =>
        flatDocumentOptions.filter(opt => categoryKeys.includes(opt.categoryKey));

    const transformApiDocsToState = (apiDocuments) => {
        const newState = {};

        apiDocuments.forEach(doc => {
            const frontendKey = apiTypeToFrontendKey[doc.document_type] || 'misc';
            const typeInfo = getDocumentTypeInfo(frontendKey);

            const newDoc = {
                id: doc.id,
                fileName: doc.document_name, // ✅ show real file name from API
                url: doc.document_url,
                type: doc.document_name.split('.').pop() || 'unknown',
                size: 'N/A',
                category: typeInfo?.categoryKey || 'others',
                isNew: false,
                instituteName: doc.Institution_name || '',
                dateOfIssue: doc.date_of_issue || '',
                certificationName: doc.certification_name || '',
                typeKey: frontendKey,
            };
            newState[frontendKey] = [...(newState[frontendKey] || []), newDoc];
        });
        return newState;
    };

    // Calculate form completion percentage
    const calculateProgress = useCallback(() => {
        const requiredDocumentTypes = [
            // Educational documents
            'metric', 'graduation', 'postGraduation', 'otherCertifications',
            // Work documents
            'offerLetter', 'appointmentLetter', 'payslips', 'experienceLetter',
            'hikeLetter', 'hikeWithPromotion', 'relocation', 'nda',
            // Identity documents
            'pan', 'aadhar', 'photo', 'drivingLicence'
        ];

        let completedCount = 0;
        let totalRequired = requiredDocumentTypes.length;

        requiredDocumentTypes.forEach(typeKey => {
            if (documents[typeKey] && documents[typeKey].length > 0) {
                completedCount++;
            }
        });

        const percent = totalRequired > 0 ? Math.round((completedCount / totalRequired) * 100) : 0;
        setCompletion(percent);
        if (typeof onFormCompletionChange === 'function') {
            onFormCompletionChange(percent);
        }
    }, [documents, onFormCompletionChange]);

    // Recalculate progress when documents change
    useEffect(() => {
        calculateProgress();
    }, [calculateProgress]);


    const fetchDocuments = useCallback(async () => {
        if (!userId) {
            setLoading(false);
            return;
        }

        if (!Token) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await getData(API_ENDPOINTS.getAllEmployeeDocumentsByUserId(userId));
            if (response.data && response.data.status === 'SUCCESS') {
                const transformedDocs = transformApiDocsToState(response.data.result);
                setDocuments(transformedDocs);
            } else {
                showSnackbar(`Failed to load documents: ${response.data?.message || 'Unknown error'}`, 'error');
                setDocuments({});
            }
        } catch (error) {
            showSnackbar("An error occurred while fetching documents.", 'error');
        } finally {
            setLoading(false);
        }
    }, [userId, Token]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const handleTypeSelect = (categoryKey, event) => {
        const newKey = event.target.value;
        const typeInfo = getDocumentTypeInfo(newKey);
        if (typeInfo?.categoryKey === categoryKey) {
            setSelectedUploadType(newKey);
        } else {
            setSelectedUploadType('');
        }
        setFileToUpload(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];

        if (!file) {
            setFileToUpload(null);
            return;
        }

        // Validate file type
        const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
        if (!validTypes.includes(file.type)) {
            showSnackbar("Please select a valid file (PDF, JPEG, JPG, or PNG).", 'error');
            setFileToUpload(null);
            event.target.value = null;
            return;
        }

        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
            showSnackbar("File size exceeds 5MB limit.", 'error');
            setFileToUpload(null);
            event.target.value = null;
            return;
        }
        setFileToUpload(file);
    };

    const handleRemoveFile = () => {
        setFileToUpload(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    const handleUploadDocument = async (categoryKey, educationalMetadata = {}, customDocName = '') => {
        if (!fileToUpload || !selectedUploadType) {
            showSnackbar("Please select a document type and a file to upload.", 'warning');
            return;
        }

        const typeInfo = getDocumentTypeInfo(selectedUploadType);

        if (!typeInfo || typeInfo.categoryKey !== categoryKey) {
            showSnackbar("Error: Document type selection mismatch.", 'error');
            return;
        }

        const isEducational = typeInfo.categoryKey === 'educational';
        if (isEducational && (!educationalMetadata.instituteName || !educationalMetadata.dateOfIssue)) {
            showSnackbar("Please enter the Institute Name and Date of Issue.", 'warning');
            return;
        }

        const formData = new FormData();

        const originalExtension = fileToUpload.name.split('.').pop();
        let renamedFileName;

        if (selectedUploadType === 'otherCertifications' || selectedUploadType === 'misc') {
            if (!customDocName.trim()) {
                showSnackbar("Please enter a name for this document.", 'warning');
                return;
            }
            const cleanCustomName = customDocName.trim().replace(/\s+/g, '_');
            renamedFileName = `${cleanCustomName}.${originalExtension}`;
        } else {
            const cleanLabel = typeInfo.label.replace(/\s+/g, '_');
            renamedFileName = `${cleanLabel}.${originalExtension}`;
        }

        const renamedFile = new File([fileToUpload], renamedFileName, { type: fileToUpload.type });

        // Append renamed file to FormData
        formData.append('files', renamedFile);
        formData.append('user_id', userId);
        formData.append('document_type', typeInfo.apiType);
        formData.append('document_name', renamedFileName);

        if (isEducational) {
            formData.append('Institution_name', educationalMetadata.instituteName);
            formData.append('date_of_issue', educationalMetadata.dateOfIssue);
        }
        setUploading(true);

        try {
            const response = await postData(API_ENDPOINTS.uploadEmployeeFiles, formData);

            if (response.data && response.data.status === 'SUCCESS') {
                showSnackbar(`Successfully uploaded: ${fileToUpload.name}`, 'success');
                await fetchDocuments();
                resetUploadState();
                setActiveUploadCategory(null);
                setTimeout(() => {
                    resetUploadState();
                }, 1000);
            } else {
                showSnackbar(`Upload failed: ${response.data?.message || 'Unknown error'}`, 'error');

            }
        } catch (error) {
            const errorMessage = error.response?.data?.message ||
                error.response?.statusText ||
                error.message ||
                'Network or server error';
            showSnackbar(`Upload failed: ${errorMessage}`, 'error');
        } finally {
            setUploading(false);
        }
    };

    const openDeleteDialog = (typeKey, doc) => {
        setDeleteDialog({
            open: true,
            docId: doc.id,
            typeKey,
            fileName: doc.fileName,
        });
    };

    const handleRemoveDocument = async () => {
        const { docId, typeKey } = deleteDialog;
        if (!docId) return;

        setLoading(true);
        try {
            const response = await deleteData(API_ENDPOINTS.deleteEmployeeDocument(docId));

            if (response.data && response.data.status === 'SUCCESS') {
                showSnackbar('Document successfully removed.', 'success');

                setDocuments(prevDocs => {
                    const updatedTypeDocs = prevDocs[typeKey].filter(doc => doc.id !== docId);
                    const { [typeKey]: removed, ...rest } = prevDocs;
                    return updatedTypeDocs.length === 0 ? rest : { ...prevDocs, [typeKey]: updatedTypeDocs };
                });
            } else {
                showSnackbar(`Removal failed: ${response.data?.message || 'Unknown error'}`, 'error');
            }
        } catch (error) {
            showSnackbar(`Removal failed. ${error.response?.data?.message || 'Network or server error'}`, 'error');
        } finally {
            setLoading(false);
            setDeleteDialog({ open: false, docId: null, typeKey: null, fileName: '' });
        }
    };

    const handleSaveChanges = () => {
        showSnackbar("Document settings saved.", 'info');
        if (navigationContext?.returnToRehire && onSaveAndReturn) {
            onSaveAndReturn();
        }
        return true;
    };

    const DocumentCard = React.memo(({ doc, typeKey }) => {
        const typeInfo = getDocumentTypeInfo(typeKey);
        const isEducational = typeInfo?.categoryKey === 'educational';

        return (
            <Card variant="outlined" sx={{ mb: 1, p: 1, backgroundColor: doc.isNew ? '#e8f5e9' : 'white' }}>
                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flexGrow={1}>
                            <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
                                {doc.fileName}
                            </Typography>

                            {isEducational && (
                                <Typography variant="caption" color="text.secondary">
                                    {doc.instituteName ? `Institute: ${doc.instituteName}` : ''}
                                    {doc.dateOfIssue ? ` | Date of Issue: ${doc.dateOfIssue}` : ''}
                                </Typography>
                            )}
                        </Box>
                        <Box>
                            <IconButton size="small" onClick={() => handlePreview(doc)} title="Preview"><VisibilityIcon fontSize="inherit" /></IconButton>
                            <IconButton size="small" onClick={() => handleDownload(doc)} title="Download"><DownloadIcon fontSize="inherit" /></IconButton>
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => openDeleteDialog(typeKey, doc)}
                                title="Remove"
                                disabled={loading}
                            >
                                <DeleteIcon fontSize="inherit" />
                            </IconButton>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        );
    });

    const DocumentSection = ({ title, categoryKey, documentOptions, isEducational = false }) => {
        const isUploadActive = activeUploadCategory === categoryKey;
        const [educationalMetadata, setEducationalMetadata] = useState({ instituteName: '', dateOfIssue: '' });
        const [customDocName, setCustomDocName] = useState('');

        useEffect(() => {
            if (!isUploadActive) {
                setEducationalMetadata({ instituteName: '', dateOfIssue: '' });
                setCustomDocName('');
            }
        }, [isUploadActive]);

        const handleEducationalMetadataChange = (event) => {
            const { name, value } = event.target;
            setEducationalMetadata(prev => ({ ...prev, [name]: value }));
        };

        const categoryDocs = documentOptions
            .filter(type => documents[type.key] && documents[type.key].length > 0)
            .flatMap(type => documents[type.key].map(doc => ({ ...doc, typeKey: type.key })))
            .sort((a, b) => b.id - a.id);

        const handleToggleUpload = () => {
            if (isUploadActive) {
                setActiveUploadCategory(null);
                resetUploadState();
            } else {
                setActiveUploadCategory(categoryKey);

            }
        };

        return (
            <Box component="section" sx={{ mb: 4, p: 3, border: '1px solid #ccc', borderRadius: 2, backgroundColor: '#fff' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight={600}>
                            {title}
                        </Typography>
                        {categoryKey === 'others' && (
                            <Tooltip
                                title="Skill related and other documents"
                                arrow
                                placement="top"
                            >
                                <InfoIcon
                                    color="info"
                                    fontSize="small"
                                    sx={{ cursor: 'pointer' }}
                                />
                            </Tooltip>
                        )}
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={isUploadActive ? <DeleteIcon /> : <AddCircleIcon />}
                        onClick={handleToggleUpload}
                        color={isUploadActive ? 'error' : 'primary'}
                        disabled={loading || uploading}
                    >
                        {isUploadActive ? 'Cancel Upload' : 'Add Document'}
                    </Button>
                </Box>

                {isUploadActive && (
                    <Box sx={{ mb: 3, p: 2, border: '1px dashed #ccc', borderRadius: 1, backgroundColor: '#f5f5f5' }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Select Document Type"
                                    value={selectedUploadType}
                                    onChange={(event) => handleTypeSelect(categoryKey, event)}
                                    disabled={loading || uploading}
                                >
                                    <MenuItem value="" disabled>
                                        Select a document type
                                    </MenuItem>
                                    {documentOptions.map((option) => (
                                        <MenuItem key={option.key} value={option.key}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid size={{ xs: 12, sm: 10 }}>
                                        <Button
                                            variant="outlined"
                                            size="medium"
                                            component="label"
                                            fullWidth
                                            startIcon={<FileUploadIcon />}
                                            disabled={!selectedUploadType || loading || uploading}
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            {fileToUpload ? fileToUpload.name : 'Choose File (Max 5MB PDF/JPEG)'}
                                        </Button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            hidden
                                            onChange={handleFileSelect}
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            disabled={loading || uploading}
                                        />
                                    </Grid>

                                    {fileToUpload && (
                                        <Grid size={{ xs: 12, sm: 1 }}>
                                            <IconButton
                                                color="error"
                                                onClick={handleRemoveFile}
                                                disabled={uploading}
                                                title="Remove file"
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                            {(selectedUploadType === 'otherCertifications' || selectedUploadType === 'misc') && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Enter Document Name"
                                        value={customDocName}
                                        onChange={(e) => setCustomDocName(e.target.value)}
                                        disabled={loading || uploading || !fileToUpload}
                                    />
                                </Grid>
                            )}
                            {isEducational && (
                                <Grid size={{ xs: 12 }}>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                fullWidth
                                                label="Institute/University Name"
                                                name="instituteName"
                                                value={educationalMetadata.instituteName}
                                                onChange={handleEducationalMetadataChange}
                                                disabled={!selectedUploadType || loading || uploading || !fileToUpload}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                fullWidth
                                                label="Date of Issue"
                                                name="dateOfIssue"
                                                type="date"
                                                value={educationalMetadata.dateOfIssue}
                                                onChange={handleEducationalMetadataChange}
                                                InputLabelProps={{ shrink: true }}
                                                disabled={!selectedUploadType || loading || uploading || !fileToUpload}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                        <Grid size={{ xs: 12 }} sm={fileToUpload ? 4 : 4} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="medium"
                                sx={{ width: 150 }} // limit width
                                onClick={() => handleUploadDocument(categoryKey, educationalMetadata, customDocName)}
                                disabled={
                                    !fileToUpload ||
                                    !selectedUploadType ||
                                    loading ||
                                    uploading ||
                                    (isEducational && (!educationalMetadata.instituteName || !educationalMetadata.dateOfIssue))
                                }
                            >
                                Upload
                            </Button>
                        </Grid>
                    </Box>
                )}

                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: 'bold' }}>
                    Existing Documents:
                </Typography>
                {loading && categoryDocs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Loading documents...
                    </Typography>
                ) : categoryDocs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No {title.toLowerCase()} uploaded yet.
                    </Typography>
                ) : (
                    categoryDocs.map(doc => <DocumentCard key={doc.id} doc={doc} typeKey={doc.typeKey} />)
                )}
            </Box>
        );
    };

    return (
        <Container maxWidth={false} disableGutters sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                    Documents
                </Typography>
                <Box sx={{ width: 220, textAlign: 'right' }}>
                    <Typography variant="body2" fontWeight={600}>
                        {`Form Completion: ${completion}%`}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={completion}
                        sx={{
                            height: 8,
                            borderRadius: 5,
                            mt: 0.5,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: completion === 100 ? '#2ecc71' : '#1976d2',
                                borderRadius: 5
                            }
                        }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Excludes miscellaneous documents
                    </Typography>
                </Box>
            </Box>

            <DocumentSection
                title={documentCategories.educational.label}
                categoryKey="educational"
                documentOptions={getCategoryOptions(['educational'])}
                isEducational={true}
            />

            <DocumentSection
                title={documentCategories.work.label}
                categoryKey="work"
                documentOptions={getCategoryOptions(['work'])}
            />

            <DocumentSection
                title={documentCategories.identity.label}
                categoryKey="identity"
                documentOptions={getCategoryOptions(['identity'])}
            />

            <DocumentSection
                title={documentCategories.others.label}
                categoryKey="others"
                documentOptions={getCategoryOptions(['others'])}
            />


            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                {navigationContext?.returnToRehire ? (
                    <Paper
                        elevation={3}
                        sx={{
                            width: '100%', p: 2, bgcolor: 'info.light', borderLeft: '4px solid',
                            borderColor: 'info.main'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Rehire Verification in Progress
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Completing verification for: <strong>{navigationContext.stepLabel || 'Documents & Other Details'}</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Document Completion: <strong>{completion}%</strong>
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                color="info"
                                onClick={() => {
                                    const success = handleSaveChanges();
                                    if (success && onSaveAndReturn) {
                                        onSaveAndReturn();
                                    }
                                }}
                                disabled={loading || uploading}
                            >
                                Verify & Return to Rehire
                            </Button>
                        </Box>
                    </Paper>
                ) : (
                    <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        onClick={handleSaveChanges}
                        sx={{ py: 1.5, px: 4 }}
                        disabled={loading || uploading}
                    >
                        Save Details
                    </Button>
                )}
            </Box>

            <AppDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, docId: null, typeKey: null, fileName: '' })}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete the document <strong>{deleteDialog.fileName}</strong>?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteDialog({ open: false, docId: null, typeKey: null, fileName: '' })}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleRemoveDocument} color="error" variant="contained" disabled={loading}>
                        Delete
                    </Button>
                </DialogActions>
            </AppDialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default EmployeeDocuments;